import Konva from 'konva';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
	OBSTRUCTION_HANDLE_ZOOM_BASELINE,
	OBSTRUCTION_HANDLE_ZOOM_SCALE_TABLE,
} from '../../../../constants';
import { RootState } from 'store';

export const OBSTRUCTION_HANDLE_HIT_STROKE = 10;
export const OBSTRUCTION_DEFAULT_FILL = 'rgba(197, 59, 34, 0.4)';
export const OBSTRUCTION_INVALID_FILL = 'rgba(117, 0, 0, 0.70)';
const MIN_OBS_HANDLE_ZOOM = 0.05;
const OBS_HANDLE_SIZE_SCALE = 0.85;
const WINDOWED_ZOOM_IN_SIZE_BOOST = 1.18;
type ObstructionHandleZoomMode = keyof typeof OBSTRUCTION_HANDLE_ZOOM_BASELINE;

function getObstructionHandleScaleData(zoom: number, mode: ObstructionHandleZoomMode) {
	const safeZoom = Math.max(MIN_OBS_HANDLE_ZOOM, zoom);
	const baselineZoom = OBSTRUCTION_HANDLE_ZOOM_BASELINE[mode];

	// Zoom-out: pick a scale by ratio of current zoom to baseline.
	if (safeZoom < baselineZoom) {
		const zoomRatio = safeZoom / baselineZoom;
		const zoomOutRanges = OBSTRUCTION_HANDLE_ZOOM_SCALE_TABLE.zoomOut;
		let scaleFactor = zoomOutRanges[zoomOutRanges.length - 1].scaleFactor;

		for (const range of zoomOutRanges) {
			scaleFactor = range.scaleFactor;
			if (zoomRatio < range.maxRatio) break;
		}

		return {
			safeZoom,
			baselineZoom,
			scaleFactor,
		};
	}

	// Zoom-in: pick a scale by offset above baseline.
	const zoomInRanges = OBSTRUCTION_HANDLE_ZOOM_SCALE_TABLE.zoomIn;
	const zoomOffset = safeZoom - baselineZoom;
	let scaleFactor: number = zoomInRanges[zoomInRanges.length - 1].scaleFactor;

	for (const range of zoomInRanges) {
		scaleFactor = range.scaleFactor;
		if (zoomOffset < range.maxOffset) break;
	}

	return {
		safeZoom,
		baselineZoom,
		scaleFactor,
	};
}

export function getObstructionHandleSize(
	base: number,
	zoom: number,
	mode: ObstructionHandleZoomMode
) {
	const { safeZoom, baselineZoom, scaleFactor } = getObstructionHandleScaleData(zoom, mode);
	// Windowed zoom-in needs a little extra size to remain usable.
	const sizeBoost = mode === 'windowed' && safeZoom > baselineZoom
		? WINDOWED_ZOOM_IN_SIZE_BOOST
		: 1;
	return (base * OBS_HANDLE_SIZE_SCALE * scaleFactor * sizeBoost) / safeZoom;
}

export const getHandleHitFunc = (
	shape: 'square' | 'circle',
	size: number,
	padding: number
) => {
	return (ctx: Konva.Context, node: Konva.Shape) => {
		ctx.beginPath();
		if (shape === 'circle') {
			ctx.arc(0, 0, size + padding, 0, Math.PI * 2);
		} else {
			ctx.rect(-padding, -padding, size + (padding * 2), size + (padding * 2));
		}
		ctx.closePath();
		ctx.fillStrokeShape(node);
	};
};

export const canStartDragFromNode = (
	node: Konva.Node | null,
	bodyNames: string[],
	blockerNames: string[]
) => {
	if (!node) return false;
	const hasName = (names: string[]) => names.some((name) => node.hasName(name));
	return hasName(bodyNames) && !hasName(blockerNames);
};

export function getCoordinatesFromShapeWithArea(
	shape: Konva.Shape,
	scaleX: number,
	scaleY: number
): { coords: number[][]; area: number } {
	let coords: number[][] = [];
	let area = 0;

	if (shape instanceof Konva.Line) {
		const points = shape.points();
		for (let i = 0; i < points.length; i += 2) {
			coords.push([points[i] / scaleX, points[i + 1] / scaleY]);
		}

		if (shape.closed()) {
			const n = coords.length;
			for (let i = 0; i < n; i++) {
				const [x1, y1] = coords[i];
				const [x2, y2] = coords[(i + 1) % n];
				area += x1 * y2 - x2 * y1;
			}
			area = Math.abs(area / 2);
		}
	} else if (shape instanceof Konva.Rect) {
		const x = shape.x();
		const y = shape.y();
		const w = shape.width();
		const h = shape.height();
		coords = [
			[x / scaleX, y / scaleY],
			[(x + w) / scaleX, y / scaleY],
			[(x + w) / scaleX, (y + h) / scaleY],
			[x / scaleX, (y + h) / scaleY],
		];
		area = (w / scaleX) * (h / scaleY);
	} else if (shape instanceof Konva.Circle) {
		const cx = shape.x();
		const cy = shape.y();
		const r = shape.radius();
		const steps = Math.min(96, Math.max(24, Math.floor(r * 0.5)));

		for (let i = 0; i < steps; i++) {
			const angle = (Math.PI * 2 * i) / steps;
			coords.push([
				(cx + r * Math.cos(angle)) / scaleX,
				(cy + r * Math.sin(angle)) / scaleY,
			]);
		}

		const rX = r / scaleX;
		const rY = r / scaleY;
		area = Math.PI * rX * rY;
	}

	return { coords, area };
}

export function flatToNested(points: number[]): number[][] {
	const out: number[][] = [];
	for (let i = 0; i < points.length; i += 2) {
		out.push([points[i], points[i + 1]]);
	}
	return out;
}

export function polygonArea(points: number[]): number {
	let area = 0;
	const n = points.length;
	for (let i = 0; i < n; i += 2) {
		const x1 = points[i];
		const y1 = points[i + 1];
		const x2 = points[(i + 2) % n];
		const y2 = points[(i + 3) % n];
		area += x1 * y2 - x2 * y1;
	}
	return Math.abs(area / 2);
}

type SegmentPoint = [number, number];
const SEGMENT_EPSILON = 1e-6;

const segmentSign = (value: number) => (value > SEGMENT_EPSILON ? 1 : value < -SEGMENT_EPSILON ? -1 : 0);

const segmentPointsEqual = (a: SegmentPoint, b: SegmentPoint) =>
	Math.abs(a[0] - b[0]) <= SEGMENT_EPSILON && Math.abs(a[1] - b[1]) <= SEGMENT_EPSILON;

const segmentCross = (a: SegmentPoint, b: SegmentPoint, c: SegmentPoint) =>
	(b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

const segmentOnSegment = (a: SegmentPoint, b: SegmentPoint, c: SegmentPoint) =>
	Math.min(a[0], c[0]) - SEGMENT_EPSILON <= b[0] &&
	b[0] <= Math.max(a[0], c[0]) + SEGMENT_EPSILON &&
	Math.min(a[1], c[1]) - SEGMENT_EPSILON <= b[1] &&
	b[1] <= Math.max(a[1], c[1]) + SEGMENT_EPSILON;

const normalizePolygonFlatPoints = (flatPoints: number[]): number[] => {
	const len = flatPoints.length;
	if (len < 6) return flatPoints;

	const eq = (i: number, j: number) =>
		Math.abs(flatPoints[i] - flatPoints[j]) <= SEGMENT_EPSILON &&
		Math.abs(flatPoints[i + 1] - flatPoints[j + 1]) <= SEGMENT_EPSILON;

	let end = len;
	while (end >= 6 && eq(0, end - 2)) end -= 2;

	let hasConsecutiveDuplicates = false;
	for (let i = 2; i < end; i += 2) {
		if (eq(i - 2, i)) {
			hasConsecutiveDuplicates = true;
			break;
		}
	}

	if (!hasConsecutiveDuplicates) {
		return end === len ? flatPoints : flatPoints.slice(0, end);
	}

	const out: number[] = [flatPoints[0], flatPoints[1]];
	for (let i = 2; i < end; i += 2) {
		if (!eq(i - 2, i)) out.push(flatPoints[i], flatPoints[i + 1]);
	}

	return out.length < 6 ? flatPoints : out;
};
const segmentsIntersect = (p1: SegmentPoint, p2: SegmentPoint, p3: SegmentPoint, p4: SegmentPoint) => {
	const d1 = segmentCross(p1, p2, p3);
	const d2 = segmentCross(p1, p2, p4);
	const d3 = segmentCross(p3, p4, p1);
	const d4 = segmentCross(p3, p4, p2);

	const s1 = segmentSign(d1);
	const s2 = segmentSign(d2);
	const s3 = segmentSign(d3);
	const s4 = segmentSign(d4);

	if (s1 * s2 < 0 && s3 * s4 < 0) return true;
	if (s1 === 0 && segmentOnSegment(p1, p3, p2)) return true;
	if (s2 === 0 && segmentOnSegment(p1, p4, p2)) return true;
	if (s3 === 0 && segmentOnSegment(p3, p1, p4)) return true;
	if (s4 === 0 && segmentOnSegment(p3, p2, p4)) return true;

	return false;
};

export function willSegmentCreateSelfIntersection(
	flatPoints: number[],
	candidatePoint: SegmentPoint,
	isClosing: boolean
) {
	const vertexCount = flatPoints.length / 2;
	if (vertexCount < 2) return false;

	const lastPoint: SegmentPoint = [flatPoints[flatPoints.length - 2], flatPoints[flatPoints.length - 1]];
	if (segmentPointsEqual(lastPoint, candidatePoint)) return false;

	const segmentCount = vertexCount - 1;

	for (let i = 0; i < segmentCount; i++) {
		// skip adjacent segments (shared endpoints)
		if (!isClosing && i === segmentCount - 1) continue;
		if (isClosing && (i === 0 || i === segmentCount - 1)) continue;

		const start: SegmentPoint = [flatPoints[i * 2], flatPoints[i * 2 + 1]];
		const end: SegmentPoint = [flatPoints[(i + 1) * 2], flatPoints[(i + 1) * 2 + 1]];

		if (segmentsIntersect(lastPoint, candidatePoint, start, end)) {
			return true;
		}
	}

	return false;
}

export function isPolygonSelfIntersecting(flatPoints: number[]) {
	const points = normalizePolygonFlatPoints(flatPoints);
	const vertexCount = points.length / 2;
	if (vertexCount < 3) return false;

	const limit = vertexCount;

	for (let i = 0; i < limit; i++) {
		const a1: SegmentPoint = [points[i * 2], points[i * 2 + 1]];
		const a2: SegmentPoint = [
			points[((i + 1) % limit) * 2],
			points[((i + 1) % limit) * 2 + 1],
		];

		for (let j = i + 1; j < limit; j++) {
			// skip adjacent edges (shared endpoints)
			if (i === j || (i + 1) % limit === j || (j + 1) % limit === i) continue;

			const b1: SegmentPoint = [points[j * 2], points[j * 2 + 1]];
			const b2: SegmentPoint = [
				points[((j + 1) % limit) * 2],
				points[((j + 1) % limit) * 2 + 1],
			];

			if (segmentsIntersect(a1, a2, b1, b2)) {
				return true;
			}
		}
	}

	return false;
}

/* ===================== RECT HELPERS ===================== */

export type RectCorner = 'TL' | 'TR' | 'BR' | 'BL';
export type RectBounds = {
minX: number;
minY: number;
maxX: number;
maxY: number;
};
type RectModel = {
	x: number;
	y: number;
	width: number;
	height: number;
};

export function pointsToRect(points: number[]): RectModel {
	const xs: number[] = [];
	const ys: number[] = [];

	for (let i = 0; i < points.length; i += 2) {
		xs.push(points[i]);
		ys.push(points[i + 1]);
	}

	const minX = Math.min(...xs);
	const maxX = Math.max(...xs);
	const minY = Math.min(...ys);
	const maxY = Math.max(...ys);

	return {
		x: minX,
		y: minY,
		width: maxX - minX,
		height: maxY - minY,
	};
}

export function rectToPoints(rect: RectModel): number[] {
	const { x, y, width, height } = rect;
	return [
		x, y,
		x + width, y,
		x + width, y + height,
		x, y + height,
	];
}

export function getAnchorFromRect(rect: RectModel, dragged: RectCorner) {
	switch (dragged) {
	case 'TL': return { x: rect.x + rect.width, y: rect.y + rect.height };
	case 'TR': return { x: rect.x, y: rect.y + rect.height };
	case 'BR': return { x: rect.x, y: rect.y };
	case 'BL': return { x: rect.x + rect.width, y: rect.y };
	}
}

/* ===================== SHAPE DETECTION ===================== */

type ObstructionShape = 'POLYGON' | 'RECTANGLE' | 'CIRCLE';

export function detectShape(points: number[]): ObstructionShape {
	if (points.length !== 8) return 'POLYGON';

	const xs = new Set<number>();
	const ys = new Set<number>();

	for (let i = 0; i < points.length; i += 2) {
		xs.add(points[i]);
		ys.add(points[i + 1]);
	}

	return xs.size === 2 && ys.size === 2 ? 'RECTANGLE' : 'POLYGON';
}

export function detectDraggedCorner(points: number[], index: number): RectCorner {
	const rect = pointsToRect(points);
	const x = points[index];
	const y = points[index + 1];

	if (x === rect.x && y === rect.y) return 'TL';
	if (x === rect.x + rect.width && y === rect.y) return 'TR';
	if (x === rect.x + rect.width && y === rect.y + rect.height) return 'BR';
	return 'BL';
}

/* ===================== EDIT POLYGON OBS ===================== */

export function updatePolygonPoint(
	points: number[],
	index: number,
	x: number,
	y: number
) {
	points[index] = x;
	points[index + 1] = y;
}

export function getCircleFromPolygon(points: number[]) {
	let sumX = 0, sumY = 0;
	const n = points.length / 2;

	for (let i = 0; i < points.length; i += 2) {
		sumX += points[i];
		sumY += points[i + 1];
	}

	const cx = sumX / n;
	const cy = sumY / n;

	let r = 0;
	for (let i = 0; i < points.length; i += 2) {
		const dx = points[i] - cx;
		const dy = points[i + 1] - cy;
		r += Math.sqrt(dx * dx + dy * dy);
	}

	return { x: cx, y: cy, radius: r / n };
}

export function circleToPolygon(
	cx: number,
	cy: number,
	radius: number,
	segments = 32
) {
	const pts: number[] = [];
	for (let i = 0; i < segments; i++) {
		const a = (i / segments) * Math.PI * 2;
		pts.push(
			cx + Math.cos(a) * radius,
			cy + Math.sin(a) * radius
		);
	}
	return pts;
}

export function getCircleHandlePoints(cx: number, cy: number, r: number) {
	return [
		{ x: cx + r, y: cy }, // 0°
		{ x: cx, y: cy - r }, // 90°
		{ x: cx - r, y: cy }, // 180°
		{ x: cx, y: cy + r }, // 270°
	];
}

export function pointsToBounds(points: number[]): RectBounds {
	const xs: number[] = [];
	const ys: number[] = [];
	for (let i = 0; i < points.length; i += 2) {
		xs.push(points[i]);
		ys.push(points[i + 1]);
	}
	return {
		minX: Math.min(...xs),
		maxX: Math.max(...xs),
		minY: Math.min(...ys),
		maxY: Math.max(...ys),
	};
}

export function boundsToPoints(b: RectBounds): number[] {
	return [
		b.minX, b.minY, // TL
		b.maxX, b.minY, // TR
		b.maxX, b.maxY, // BR
		b.minX, b.maxY, // BL
	];
}

export function getCorners(b: RectBounds) {
	return {
		TL: { x: b.minX, y: b.minY },
		TR: { x: b.maxX, y: b.minY },
		BR: { x: b.maxX, y: b.maxY },
		BL: { x: b.minX, y: b.maxY },
	};
}

export function oppositeCorner(c: RectCorner): RectCorner {
	switch (c) {
	case 'TL': return 'BR';
	case 'TR': return 'BL';
	case 'BR': return 'TL';
	case 'BL': return 'TR';
	}
}

export function simplifyCoordsByDistance(
	points: number[],
	minDistancePx: number
): number[] {
	if (points.length <= 4) return points;

	const simplified: number[] = [];

	let lastX = points[0];
	let lastY = points[1];

	simplified.push(lastX, lastY);

	for (let i = 2; i < points.length; i += 2) {
		const x = points[i];
		const y = points[i + 1];

		const dx = x - lastX;
		const dy = y - lastY;
		const dist = Math.sqrt(dx * dx + dy * dy);

		if (dist >= minDistancePx) {
			simplified.push(x, y);
			lastX = x;
			lastY = y;
		}
	}

	return simplified;
}

export const areCoordinatesEqual = (coords1: any[], coords2: any[]): boolean => {
	if (!coords1 || !coords2) return false;
	const flat1 = coords1.flat(Infinity) as number[];
	const flat2 = coords2.flat(Infinity) as number[];
	if (flat1.length !== flat2.length) return false;

	return flat1.every((val, i) => Math.abs(val - flat2[i]) < 0.001);
};
export function useShowPolygonObsPointStrip() {
	const {
		enableEditObs,
		deleteObstructionsMode,
		selectedObstructionId,
		editedObstructions,
		manualObs
	} = useSelector((state: RootState) => state.toolNewPostions.data);
	const { obstructions } = useSelector((state: RootState) => state.roofData.data);

	return useMemo(() => {
		if (!enableEditObs || deleteObstructionsMode || !selectedObstructionId) {
			return false;
		}

		const obs = obstructions.find((o) => o.id === selectedObstructionId);
		const manual = manualObs[selectedObstructionId];

		if (!obs && !manual) return false;

		const assessment = obs
			? (editedObstructions?.[obs.id]?.assessment ?? obs.assessment)
			: manual?.shapeType;

		if (assessment === 'CIRCLE') return false;

		const coords = obs
			? (editedObstructions?.[obs.id]?.coordinates ?? obs.coordinates)
			: (manual?.coordinates ?? []);

		return detectShape(coords.flat()) === 'POLYGON';
	}, [
		enableEditObs,
		deleteObstructionsMode,
		selectedObstructionId,
		editedObstructions,
		manualObs,
		obstructions
	]);
}

export function isPointInPolygon(point: { x: number; y: number }, polygon: number[]) {
	let inside = false;

	for (let i = 0, j = polygon.length / 2 - 1; i < polygon.length / 2; j = i++) {
		const xi = polygon[i * 2];
		const yi = polygon[i * 2 + 1];
		const xj = polygon[j * 2];
		const yj = polygon[j * 2 + 1];

		const intersect =
			yi > point.y !== yj > point.y &&
			point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

		if (intersect) inside = !inside;
	}

	return inside;
}

export function getAbsolutePoints(node: Konva.Node, points: number[]) {
	const transform = node.getAbsoluteTransform();

	const result: number[] = [];

	for (let i = 0; i < points.length; i += 2) {
		const pos = transform.point({
			x: points[i],
			y: points[i + 1],
		});

		result.push(pos.x, pos.y);
	}

	return result;
}
